import { useState, useEffect, useLayoutEffect } from "react";
import { Modal } from "react-bootstrap";
import ProfileIcon from "../../../../assets/images/Profile.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  resetOnBoardingStartContext,
  storeUser,
  updateUser,
} from "../../../../redux/users/usersSlice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { setLoading, setShowConnectionSuccessModal } from "../../../../redux/loading/loadingSlice";
import { toastify } from "../../../../helpers/toast/toastify";
import StripePaymentModal from "../../../sharedPages/StripePaymentModal";
import BubbleSvgButton from "../../../sharedPages/CommonComponents/BubbleSvgButton/BubbleSvgButton";
import PaymentSuccessModal from "../../../sharedPages/PaymentSuccessModal";
import errorGif from "../../../../assets/images/error.gif";
import longArrow from "../../../../assets/images/onboardingImages/longArrow.svg";
import StripPayment from "../../../../assets/images/power-by-stripe.png";
import LoadingOverlayModal from "../../../sharedPages/LoadingOverlayModal";
import VipVerificationPopUpCM from "../../../sharedPages/VipScreen/VipVerificationCM";
import { CHANGE_COMMUNITY_FLOW_KEY } from "./MemberSubscribeChangeCommunity";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const SubscribeModal = () => {
  const [billing, setBilling] = useState("monthly");
  const [selected, setSelected] = useState(0);
  const [clientSecret, setClientSecret] = useState("");
  const [regularPlans, setRegularPlans] = useState({ monthly: [], yearly: [] });
  const [show, setShow] = useState(true);
  const [vipCommunityData, setVipCommunityData] = useState(null);
  // Payment flow states
  const [paymentSuccessView, setPaymentSuccessView] = useState(false);
  const [paymentFailView, setPaymentFailView] = useState(false);
  const [displayOverlay, setDisplayOverlay] = useState(false);
  const [notShowPaymentDetail, setNotShowPaymentDetail] = useState(false);

  // VIP states (mirrored from Subscribe.jsx)
  const [vipSubscription, setVipSubscription] = useState(null);
  const [isVipSubscriptionSubscribed, setIsVipSubscriptionSubscribed] = useState({
    monthly: false,
    yearly: false,
  });
  const [vipCheckBoxClick, setVipCheckBoxClick] = useState({
    monthly: false,
    yearly: false,
  });
  const [anySubscriptionSubscribe, setAnySubscriptionSubscribe] = useState(false);
  const [vipButtonText, setVipButtonText] = useState({
    monthly: "Subscribe for $15 / month",
    yearly: "Subscribe for $180 / year",
  });
  const [showVipPopUp, setShowVipPopUp] = useState({ show: false });

  // Subscription selection state
  const [subId, setSubId] = useState("");
  const [fullcommunity, setFullCommunity] = useState(null);
  const [planSub, setPlanSub] = useState(false);

  const community = useSelector(
    (value) => value?.user?.user.memberCommunities[0].community,
  );
  const user = useSelector((state) => state?.user?.user);
  const onBoardingStartContext = useSelector(
    (state) => state.user?.onBoardingStartContext,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const api = useApi();

  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");

  // ─── Build regular + vip plans from real subscription data ───────────────
  useEffect(() => {
    const allSubscription =
      user?.memberCommunities?.[0]?.community?.subscription || [];

    const activeSubscriptions = allSubscription
      .filter((sub) => sub.isActive && !sub.name.endsWith("_Vip"))
      .sort((a, b) => a.name.localeCompare(b.name));

    const monthly = activeSubscriptions.map((sub) => ({
      price: `$${Number(sub.monthlyfee) + 15}`,
      period: "MONTH",
      max:
        sub.tradingCapital?.toString().toUpperCase() === "UNLIMITED"
          ? "UNLIMITED"
          : `$${Number(sub.tradingCapital || 0).toLocaleString()}`,
      originalData: sub,
    }));

    const yearly = activeSubscriptions.map((sub) => ({
      price: `$${Number(sub.yearlyfee) + 180}`,
      period: "YEAR",
      max:
        sub.tradingCapital?.toString().toUpperCase() === "UNLIMITED"
          ? "UNLIMITED"
          : `$${Number(sub.tradingCapital || 0).toLocaleString()}`,
      originalData: sub,
    }));

    setRegularPlans({ monthly, yearly });
  }, [user?.memberCommunities]);
  

  // ─── VIP subscription status logic (exactly from Subscribe.jsx) ──────────
  useEffect(() => {
    const allSubscription =
      user?.memberCommunities?.[0]?.community?.subscription || [];

    const anySubscribed = user?.memberCommunities?.[0]?.subscribed;
    const vipSub = allSubscription.filter(
      (sub) => sub.name.endsWith("_Vip") && sub.isAllowed,
    )[0];

    const isVipSubscribed =
      vipSub?._id === user?.memberCommunities?.[0]?.subscription && anySubscribed;

    const planType = user?.memberCommunities?.[0]?.planType;

    setAnySubscriptionSubscribe(anySubscribed);
    setVipSubscription(vipSub);

    if (isVipSubscribed && planType === "monthly") {
      setVipButtonText({ monthly: "Cancel", yearly: "Upgrade" });
      setVipCheckBoxClick({ monthly: true, yearly: false });
      setIsVipSubscriptionSubscribed({ monthly: true, yearly: false });
    } else if (isVipSubscribed && planType === "yearly") {
      setVipButtonText({ monthly: "Upgrade", yearly: "Cancel" });
      setVipCheckBoxClick({ monthly: false, yearly: true });
      setIsVipSubscriptionSubscribed({ monthly: false, yearly: true });
    } else if (anySubscribed) {
      setVipButtonText({ monthly: "Upgrade", yearly: "Upgrade" });
      setVipCheckBoxClick({ monthly: false, yearly: false });
      setIsVipSubscriptionSubscribed({ monthly: false, yearly: false });
    } else {
      setVipCheckBoxClick({ monthly: false, yearly: false });
      setIsVipSubscriptionSubscribed({ monthly: false, yearly: false });
      setVipButtonText({
        monthly: "Subscribe for $15 / month",
        yearly: "Subscribe for $180 / year",
      });
    }
  }, [user?.memberCommunities]);

  // ─── Track subscribed community (needed for cancel/upgrade logic) ─────────
  useEffect(() => {
    user?.memberCommunities?.forEach((com) => {
      if (com?.subscribed) {
        setFullCommunity(com);
        setPlanSub(true);
      } else {
        setFullCommunity(com);
      }
    });
  }, [user?.memberCommunities]);

  // ─── Redirect if already paid ─────────────────────────────────────────────
  useEffect(() => {
    // BUG: onboarding.payment is a one-time "has this user ever paid" flag —
    // it stays true forever after their first community subscription, so a
    // member who unsubscribed and picked a NEW community from MarketPlace
    // got bounced straight to the dashboard here instead of ever seeing
    // this pricing/payment screen for the new community.
    // if (user?.onboarding?.payment) {
    //   navigate("/member/dashboard", { replace: true });
    // } else {
    //   setShow(true);
    // }
    if (user?.memberCommunities?.[0]?.subscribed) {
      navigate("/member/dashboard", { replace: true });
    } else {
      setShow(true);
    }
  }, []);

  // ─── Handle return from Stripe (session_id in URL) ────────────────────────
  const createSubscription = async () => {
    try {
      dispatch(setLoading(true));
      setDisplayOverlay(true);
      setShow(false);
      const response = await api("post", "users/handlePayments", {
        sessionId,
        communityId: id,
      });
      if (response?.status) {
        setPaymentSuccessView(true);
        setDisplayOverlay(false);
        dispatch(updateUser(response?.data?.user));
        dispatch(setShowConnectionSuccessModal(true));
        const responseUser = await api("post", "users/update", {
          ...(user?.firstVisit ? { onboard: "done" } : {}),
          onboarding: { ...user.onboarding, payment: true },
        });
        dispatch(updateUser(responseUser?.data?.user));
        dispatch(setLoading(false));
        // Stripe's embedded checkout return_url is fixed server-side and
        // always redirects here (/member/subscribe/:id), even when checkout
        // was actually started from the "change community" payment screen
        // (MemberSubscribeChangeCommunity.jsx) — the backend has no notion
        // of that separate frontend route. That screen sets this flag right
        // before handing off to Stripe so we can route to the matching
        // completion screen instead of always assuming a new member.
        const isChangeCommunityFlow =
          localStorage.getItem(CHANGE_COMMUNITY_FLOW_KEY) === "true";
        localStorage.removeItem(CHANGE_COMMUNITY_FLOW_KEY);
        setTimeout(() => {
          setPaymentSuccessView(false);
          setShow(true);
          dispatch(setShowConnectionSuccessModal(false));
          if (isChangeCommunityFlow) {
            navigate("/member/changeCommunity/done", {
              replace: true,
              state: { from: "onboarding" },
            });
          } else {
            navigate("/member/onboarding/done", {
              replace: true,
              state: { from: "onboarding" },
            });
          }
        }, 2000);
        toastify("You have been subscribed successfully.", "success");
      } else {
        dispatch(setLoading(false));
        setDisplayOverlay(false);
        setPaymentFailView(true);
        const isChangeCommunityFlow =
          localStorage.getItem(CHANGE_COMMUNITY_FLOW_KEY) === "true";
        localStorage.removeItem(CHANGE_COMMUNITY_FLOW_KEY);
        setTimeout(() => {
          setPaymentFailView(false);
          navigate(
            isChangeCommunityFlow
              ? "/member/changeCommunity/subscription"
              : "/member/subscribe",
            { replace: true }
          );
        }, 2000);
      }
    } catch (err) {
      console.log(err);
      dispatch(setLoading(false));
      setDisplayOverlay(false);
    }
  };

  useEffect(() => {
    if (id && sessionId) {
      createSubscription();
    }
  }, []);

  useLayoutEffect(() => {
    if (id) {
      setNotShowPaymentDetail(true);
    }
  }, []);

  const handlePaymentFailView = async () => {
    const responseUser = await api("post", "users/update");
    setTimeout(() => {
      setPaymentFailView(false);
      setNotShowPaymentDetail(false);
      navigate("/member/copyTrade", { replace: true });
    }, 0);
  };

  // ─── Plan data ────────────────────────────────────────────────────────────
  const currentRegularPlans = regularPlans[billing] || [];

  // The currently selected regular plan (by index)
  const activePlan = vipCheckBoxClick[billing]
    ? null
    : currentRegularPlans[selected];

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getNextBillingDate = () => {
    const date = new Date();
    if (billing === "monthly") {
      date.setMonth(date.getMonth() + 1);
    } else {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (onBoardingStartContext) {
      navigate(onBoardingStartContext, { replace: true });
      dispatch(resetOnBoardingStartContext());
      return;
    }
    navigate("/member/dashboard", { replace: true });
  };

  const handleSelectRegular = (index) => {
    if (anySubscriptionSubscribe) return; // already subscribed — no switching
    setSelected(index);
    setVipCheckBoxClick({ monthly: false, yearly: false });
    if (currentRegularPlans[index]?.originalData) {
      setSubId(currentRegularPlans[index].originalData._id);
    }
  };

  const handleSelectVip = () => {
    if (anySubscriptionSubscribe) return; // already subscribed to something
    setSelected(null);
    setVipCheckBoxClick({
      monthly: billing === "monthly",
      yearly: billing === "yearly",
    });
  };

  // Regular plan checkout → Stripe
  const handleSubscribe = async () => {
    if (!activePlan) return;
    dispatch(setLoading(true));
    try {
      const response = await api("post", "users/checkoutSession", {
        communityId: user?.memberCommunities?.[0]?.community?._id,
        subscription: activePlan.originalData,
        plan: billing,
        onboardingCM: true,
      });
      if (response?.status) {
        // Clear a stale flag from an abandoned "change community" checkout
        // in this browser, so it doesn't wrongly route this (new-member)
        // payment to the change-community completion screen.
        localStorage.removeItem(CHANGE_COMMUNITY_FLOW_KEY);
        setClientSecret(response?.data?.clientSecret);
        dispatch(setLoading(false));
      } else {
        toastify(response?.message);
        dispatch(setLoading(false));
      }
    } catch {
      toastify("Something went wrong. Please try again.");
      dispatch(setLoading(false));
    }
  };

  // VIP plan checkout → VipVerificationPopUp → Stripe
  // Replace handleVipSubscribe with this:
  const handleVipSubscribe = () => {
   if (isVipSubscriptionSubscribed?.[billing]) {
    navigate("/member/subscribe", { replace: true });
   } else {
    // Store the VIP data we'll need for checkout
    setVipCommunityData({
      communityId: user?.memberCommunities?.[0]?.community?._id,
      subscription: vipSubscription,
      plan: billing,
    });
    setShowVipPopUp({ show: true });
  }
};
const handleVipCheckout = async () => {
  if (!vipCommunityData) return;
  dispatch(setLoading(true));
  try {
    const response = await api("post", "users/checkoutSession", {
      communityId: vipCommunityData.communityId,
      subscription: vipCommunityData.subscription,
      plan: vipCommunityData.plan,
      onboardingCM: true,
    });
    if (response?.status) {
      localStorage.removeItem(CHANGE_COMMUNITY_FLOW_KEY);
      setClientSecret(response?.data?.clientSecret); // This triggers StripePaymentModal inline
      dispatch(setLoading(false));
    } else {
      toastify(response?.message);
      dispatch(setLoading(false));
    }
  } catch {
    toastify("Something went wrong. Please try again.");
    dispatch(setLoading(false));
  }
};
  // Derive the subscribe button label
  const getButtonLabel = () => {
    if (vipCheckBoxClick[billing]) {
      return vipButtonText[billing];
    }
    if (!activePlan) return "Select a plan";
    return `Subscribe for ${activePlan.price} / ${billing === "monthly" ? "month" : "year"}`;
  };

  // Is the current selection in "cancel" mode?
  const isCancelMode = vipCheckBoxClick[billing]
    ? isVipSubscriptionSubscribed?.[billing]
    : planSub &&
      billing === fullcommunity?.planType &&
      (fullcommunity?.subscribedPlan === activePlan?.originalData?.yearlyPlanId ||
        fullcommunity?.subscribedPlan === activePlan?.originalData?.monthlyPlanId);

  // ─── Early-return states ──────────────────────────────────────────────────
  if (displayOverlay) return <LoadingOverlayModal />;
  if (paymentSuccessView) return <PaymentSuccessModal />;
  if (paymentFailView) {
    return (
      <div className="connectLoader">
        <div className="text-center">
          <h3 className="fs20 li_h30 bold">Oops. Something went wrong : &#40;</h3>
          <p className="fs14 mb-26">Please try again</p>
          <img src={errorGif} alt="loaderGif" height={248} />
          <br />
          <button
            style={{ width: "294px" }}
            className="newCreateButton"
            onClick={handlePaymentFailView}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="onboarding-modal-wrapper">
        <Modal
          animation={false}
          backdropClassName="onboarding-custom-backdrop"
          centered
          dialogClassName="custom-onboarding-modal verifyTrdeInfo"
          show={show && !clientSecret}
        >
          <Modal.Body
            className="figma-drop-shadow"
            style={{ padding: "28px 10px 32px" }}
          >
            {/* Back arrow */}
            <button
              className="reverseArrow"
              onClick={() =>
                navigate("/member/onboarding/connection", { replace: true })
              }
              type="button"
            >
              <img alt="" src={longArrow} />
            </button>

            {/* Close */}
            <button className="CrossIcon" onClick={handleClose} type="button">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M1 1L14 14M14 1L1 14"
                  stroke="#000"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Brand row */}
            <div className="onboarding__header">
              <div className="onboarding_header-avater">
                {community?.logo ? (
                  <img
                    src={community.logo}
                    alt={`${community?.disPlayName} logo`}
                    style={{
                      width: "35px",
                      height: "35px",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  <img
                    src={ProfileIcon}
                    alt="community logo"
                    style={{
                      width: "35px",
                      height: "35px",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </div>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#000",
                  fontFamily:
                    "Graphik Bold, -apple-system, BlinkMacSystemFont, sans-serif",
                }}
              >
                {community?.disPlayName?.toUpperCase()}
              </span>
            </div>

            {/* Title */}
            <div className="verifyTradeContent">
              <h2 className="title">Subscribe to our service</h2>
              <p className="desc tradeDesc">
                You need to be subscribed to copy our trades
              </p>
            </div>

            {/* Monthly / Yearly toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 22,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "0",
                  marginBottom: "32px",
                  width: "169px",
                  padding: "5px 7px",
                  borderRadius: "20px",
                  backgroundColor: "#EDEDED",
                }}
              >
                {["monthly", "yearly"].map((type) => (
                  <button
                    key={type}
                    style={{
                      width: "7rem",
                      fontSize: "12px",
                      borderRadius: "12px",
                      borderColor: "transparent",
                      ...(billing === type
                        ? { backgroundColor: "#000000", color: "#ffffff" }
                        : { backgroundColor: "transparent", color: "#000" }),
                    }}
                    onClick={() => {
                      setBilling(type);
                      // Reset VIP checkbox when switching tabs (same as Subscribe.jsx)
                      setVipCheckBoxClick({ monthly: false, yearly: false });
                      setSelected(0);
                    }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Plan cards */}
            <ul
              className="infoList"
              style={{
                listStyle: "none",
                padding: 0,
                margin: "0 auto 0",
                maxWidth: 440,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {/* ── Regular plans ── */}
              {currentRegularPlans.map((plan, i) => {
                const isSelected = !vipCheckBoxClick[billing] && selected === i;
                return (
                  <li
                    key={i}
                    onClick={() => handleSelectRegular(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 18px",
                      boxShadow: "10px 24px 54px 0px rgba(0, 0, 0, 0.059)",
                      borderRadius: "10px",
                      cursor: anySubscriptionSubscribe
                        ? "not-allowed"
                        : "pointer",
                      background: "#fff",
                      gap: 5,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "#000",
                      opacity: anySubscriptionSubscribe ? 0.6 : 1,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#000",
                          marginBottom: 3,
                          textAlign: "start",
                        }}
                      >
                        {plan.price} / {plan.period}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#888",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        TRADE WITH MAX{plan.max ? `: ${plan.max}` : ""}
                      </div>
                    </div>

                    {isSelected ? (
                      <div
                        style={{
                          width: 25,
                          height: 25,
                          borderRadius: "50%",
                          background: "#22C55E",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="15"
                          height="11"
                          viewBox="0 0 15 11"
                          fill="none"
                        >
                          <path
                            d="M1 5.5L5 9.5L14 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 25,
                          height: 25,
                          borderRadius: "50%",
                          border: "1.8px solid #C8C8C8",
                          background: "transparent",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </li>
                );
              })}

              {/* ── VIP plan card (only rendered when vipSubscription exists) ── */}
              {vipSubscription && (
                <li
                  onClick={handleSelectVip}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 18px",
                    boxShadow: "10px 24px 54px 0px rgba(0, 0, 0, 0.059)",
                    borderRadius: "10px",
                    cursor: anySubscriptionSubscribe
                      ? "not-allowed"
                      : "pointer",
                    background: "#fff",
                    gap: 5,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: "#000",
                    opacity: anySubscriptionSubscribe ? 0.6 : 1,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#000",
                        marginBottom: 3,
                        textAlign: "start",
                      }}
                    >
                      {billing === "monthly" ? "$15 / MONTH" : "$180 / YEAR"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#888",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      VIP ACCESS
                    </div>
                  </div>

                  {vipCheckBoxClick[billing] ? (
                    <div
                      style={{
                        width: 25,
                        height: 25,
                        borderRadius: "50%",
                        background: "#22C55E",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="15"
                        height="11"
                        viewBox="0 0 15 11"
                        fill="none"
                      >
                        <path
                          d="M1 5.5L5 9.5L14 1"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: 25,
                        height: 25,
                        borderRadius: "50%",
                        border: "1.8px solid #C8C8C8",
                        background: "transparent",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </li>
              )}
            </ul>

            {/* Fine print */}
            <p
              style={{
                fontSize: 10,
                lineHeight: "15px",
                fontWeight: 600,
                color: "#A09D9D",
                textAlign: "center",
                margin: "16px auto 20px",
                maxWidth: 300,
              }}
            >
              Free 7-day trial. Unlimited trades. Cancel or upgrade subscription
              anytime. Auto-renews{" "}
              {billing === "monthly" ? "monthly" : "yearly"}.
            </p>

            {/* Subscribe button */}
            <div
              className="d-sm-flex justify-content-center align-items-center flex-column"
              style={{ gap: "20px", padding: "0 10px" }}
            >
              <BubbleSvgButton
                className="ExtraTierBtn"
                text={getButtonLabel()}
                type="button"
                disabled={!vipCheckBoxClick[billing] && !activePlan}
                variant={isCancelMode ? "red" : "black"}
                onClick={() => {
                  if (vipCheckBoxClick[billing]) {
                    handleVipSubscribe();
                  } else {
                    handleSubscribe();
                  }
                }}
                containerStyling={{ justifyContent: "center" }}
                mainDivStyle={{ width: "295px", height: "55px" }}
              />

              <div>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#666666",
                    marginRight: "4px",
                  }}
                >
                  Safe and Secure Payments Checkout
                </p>
                <div className="stripePayments">
                  <img src={StripPayment} alt="payments" />
                </div>
              </div>
            </div>

            {/* Pagination dots */}
            <div
              className="paginationDots"
              style={{ position: "static", marginTop: "30px" }}
            >
              <button className="dot" type="button" />
              <button className="dot" type="button" />
              <button className="dot active" type="button" />
              <button className="dot" type="button" />
            </div>
          </Modal.Body>
        </Modal>
      </div>
      {clientSecret && (
          <StripePaymentModal
            setClientSecret={setClientSecret}
            clientSecret={clientSecret}
          />
        )}
      {/* Stripe embedded checkout */}
        

      {/* VIP verification popup — mirrors Subscribe.jsx exactly */}
      {showVipPopUp?.show && (
        <VipVerificationPopUpCM
          setShowVipPopUp={setShowVipPopUp}
          callApi={() => {
            setShowVipPopUp({ show: false });
            handleVipCheckout(); // Call checkout directly → sets clientSecret → opens StripePaymentModal inline
          }}
        />
      )}
    </>
  );
};

export default SubscribeModal;